import { supabase as _supabase } from './supabase';
import type {
  Program,
  ProgramWithStats,
  ProgramActivity,
  ProgramAssignment,
  ProgramActivityProgress,
  ProgramActivityResponse,
  ProgramDetail,
  AssignedProgram,
  AssigneeOption,
  ActivityType,
  ActivityStatus,
} from '../types/program';

const db = _supabase as any;

function mapProgram(row: any): Program {
  return {
    id: row.id,
    title: row.title,
    description: row.description,
    creatorUserId: row.creator_user_id,
    creatorType: row.creator_type,
    isSequential: row.is_sequential,
    isTemplate: row.is_template,
    isPublished: row.is_published,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

function mapActivity(row: any): ProgramActivity {
  return {
    id: row.id,
    programId: row.program_id,
    position: row.position,
    activityType: row.activity_type as ActivityType,
    title: row.title,
    instructions: row.instructions,
    resourceUrl: row.resource_url,
    dueDate: row.due_date,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

function mapAssignment(row: any): ProgramAssignment {
  return {
    id: row.id,
    programId: row.program_id,
    assigneeUserId: row.assignee_user_id,
    assigneeType: row.assignee_type,
    assignedAt: row.assigned_at,
    completedAt: row.completed_at,
    assigneeName: row.assignee_name,
    assigneeEmail: row.assignee_email,
  };
}

function mapProgress(row: any): ProgramActivityProgress {
  return {
    id: row.id,
    assignmentId: row.assignment_id,
    activityId: row.activity_id,
    status: row.status as ActivityStatus,
    startedAt: row.started_at,
    completedAt: row.completed_at,
  };
}

function mapResponse(row: any): ProgramActivityResponse {
  return {
    id: row.id,
    progressId: row.progress_id,
    activityId: row.activity_id,
    responseText: row.response_text,
    uploadPath: row.upload_path,
    uploadFilename: row.upload_filename,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

// ============================================================
// PROGRAM CRUD
// ============================================================

export async function fetchPrograms(creatorUserId: string): Promise<ProgramWithStats[]> {
  const { data: programs } = await db
    .from('programs')
    .select('*')
    .eq('creator_user_id', creatorUserId)
    .order('created_at', { ascending: false });

  if (!programs || programs.length === 0) return [];

  const programIds = programs.map((p: any) => p.id);

  const [activitiesRes, assignmentsRes] = await Promise.all([
    db.from('program_activities').select('program_id').in('program_id', programIds),
    db.from('program_assignments').select('program_id').in('program_id', programIds),
  ]);

  const activityCounts = new Map<string, number>();
  (activitiesRes.data || []).forEach((a: any) => {
    activityCounts.set(a.program_id, (activityCounts.get(a.program_id) || 0) + 1);
  });

  const assignmentCounts = new Map<string, number>();
  (assignmentsRes.data || []).forEach((a: any) => {
    assignmentCounts.set(a.program_id, (assignmentCounts.get(a.program_id) || 0) + 1);
  });

  return programs.map((row: any) => ({
    ...mapProgram(row),
    activityCount: activityCounts.get(row.id) || 0,
    assignmentCount: assignmentCounts.get(row.id) || 0,
  }));
}

export async function createProgram(
  creatorUserId: string,
  creatorType: 'coach' | 'partner',
  data: { title: string; description: string; isSequential: boolean; isTemplate: boolean }
): Promise<string> {
  const { data: row, error } = await db
    .from('programs')
    .insert({
      creator_user_id: creatorUserId,
      creator_type: creatorType,
      title: data.title,
      description: data.description,
      is_sequential: data.isSequential,
      is_template: data.isTemplate,
    })
    .select('id')
    .single();

  if (error) throw error;
  return row.id;
}

export async function updateProgram(
  programId: string,
  data: Partial<{ title: string; description: string; isSequential: boolean; isTemplate: boolean; isPublished: boolean }>
): Promise<void> {
  const payload: any = { updated_at: new Date().toISOString() };
  if (data.title !== undefined) payload.title = data.title;
  if (data.description !== undefined) payload.description = data.description;
  if (data.isSequential !== undefined) payload.is_sequential = data.isSequential;
  if (data.isTemplate !== undefined) payload.is_template = data.isTemplate;
  if (data.isPublished !== undefined) payload.is_published = data.isPublished;

  const { error } = await db.from('programs').update(payload).eq('id', programId);
  if (error) throw error;
}

export async function deleteProgram(programId: string): Promise<void> {
  const { error } = await db.from('programs').delete().eq('id', programId);
  if (error) throw error;
}

export async function fetchProgramDetail(programId: string): Promise<ProgramDetail | null> {
  const { data: programRow } = await db
    .from('programs')
    .select('*')
    .eq('id', programId)
    .maybeSingle();

  if (!programRow) return null;

  const { data: activityRows } = await db
    .from('program_activities')
    .select('*')
    .eq('program_id', programId)
    .order('position');

  return {
    ...mapProgram(programRow),
    activities: (activityRows || []).map(mapActivity),
  };
}

// ============================================================
// ACTIVITY CRUD
// ============================================================

export async function addActivity(
  programId: string,
  data: {
    activityType: ActivityType;
    title: string;
    instructions: string;
    resourceUrl?: string;
    dueDate?: string | null;
    position: number;
  }
): Promise<string> {
  const { data: row, error } = await db
    .from('program_activities')
    .insert({
      program_id: programId,
      position: data.position,
      activity_type: data.activityType,
      title: data.title,
      instructions: data.instructions,
      resource_url: data.resourceUrl || '',
      due_date: data.dueDate || null,
    })
    .select('id')
    .single();

  if (error) throw error;
  return row.id;
}

export async function updateActivity(
  activityId: string,
  data: Partial<{
    activityType: ActivityType;
    title: string;
    instructions: string;
    resourceUrl: string;
    dueDate: string | null;
    position: number;
  }>
): Promise<void> {
  const payload: any = { updated_at: new Date().toISOString() };
  if (data.activityType !== undefined) payload.activity_type = data.activityType;
  if (data.title !== undefined) payload.title = data.title;
  if (data.instructions !== undefined) payload.instructions = data.instructions;
  if (data.resourceUrl !== undefined) payload.resource_url = data.resourceUrl;
  if (data.dueDate !== undefined) payload.due_date = data.dueDate;
  if (data.position !== undefined) payload.position = data.position;

  const { error } = await db.from('program_activities').update(payload).eq('id', activityId);
  if (error) throw error;
}

export async function deleteActivity(activityId: string): Promise<void> {
  const { error } = await db.from('program_activities').delete().eq('id', activityId);
  if (error) throw error;
}

export async function reorderActivities(programId: string, orderedIds: string[]): Promise<void> {
  const updates = orderedIds.map((id, index) =>
    db.from('program_activities').update({ position: index, updated_at: new Date().toISOString() }).eq('id', id)
  );
  await Promise.all(updates);
}

// ============================================================
// ASSIGNMENTS
// ============================================================

export async function fetchProgramAssignments(programId: string): Promise<ProgramAssignment[]> {
  const { data: assignments } = await db
    .from('program_assignments')
    .select('*')
    .eq('program_id', programId)
    .order('assigned_at', { ascending: false });

  if (!assignments || assignments.length === 0) return [];

  const userIds = assignments.map((a: any) => a.assignee_user_id);
  const { data: users } = await db
    .from('users')
    .select('id, full_name, email')
    .in('id', userIds);

  const userMap = new Map<string, { name: string; email: string }>();
  (users || []).forEach((u: any) => {
    userMap.set(u.id, { name: u.full_name || u.email || 'Unknown', email: u.email || '' });
  });

  return assignments.map((a: any) => {
    const info = userMap.get(a.assignee_user_id) || { name: 'Unknown', email: '' };
    return { ...mapAssignment(a), assigneeName: info.name, assigneeEmail: info.email };
  });
}

export async function assignProgram(
  programId: string,
  assigneeUserId: string,
  assigneeType: 'coach' | 'respondent'
): Promise<void> {
  const { error } = await db.from('program_assignments').insert({
    program_id: programId,
    assignee_user_id: assigneeUserId,
    assignee_type: assigneeType,
  });
  if (error) throw error;
}

export async function unassignProgram(programId: string, assigneeUserId: string): Promise<void> {
  const { error } = await db
    .from('program_assignments')
    .delete()
    .eq('program_id', programId)
    .eq('assignee_user_id', assigneeUserId);
  if (error) throw error;
}

// ============================================================
// ASSIGNEE LISTS
// ============================================================

export async function fetchCoachRespondentsForAssignment(coachUserId: string): Promise<AssigneeOption[]> {
  const { data: coach } = await db
    .from('coaches')
    .select('id')
    .eq('user_id', coachUserId)
    .maybeSingle();

  if (!coach) return [];

  const { data: respondents } = await db
    .from('respondents')
    .select('user_id')
    .eq('coach_id', coach.id);

  if (!respondents || respondents.length === 0) return [];

  const userIds = respondents.map((r: any) => r.user_id);
  const { data: users } = await db
    .from('users')
    .select('id, full_name, email')
    .in('id', userIds);

  return (users || []).map((u: any) => ({
    userId: u.id,
    name: u.full_name || u.email || 'Unknown',
    email: u.email || '',
    type: 'respondent' as const,
  }));
}

export async function fetchPartnerCoachesForAssignment(partnerUserId: string): Promise<AssigneeOption[]> {
  const { data: partner } = await db
    .from('partners')
    .select('id')
    .eq('user_id', partnerUserId)
    .maybeSingle();

  if (!partner) return [];

  const { data: coaches } = await db
    .from('coaches')
    .select('user_id')
    .eq('partner_id', partner.id);

  if (!coaches || coaches.length === 0) return [];

  const userIds = coaches.map((c: any) => c.user_id);
  const { data: users } = await db
    .from('users')
    .select('id, full_name, email')
    .in('id', userIds);

  return (users || []).map((u: any) => ({
    userId: u.id,
    name: u.full_name || u.email || 'Unknown',
    email: u.email || '',
    type: 'coach' as const,
  }));
}

// ============================================================
// RESPONDENT/ASSIGNEE VIEWS
// ============================================================

export async function fetchMyPrograms(assigneeUserId: string): Promise<AssignedProgram[]> {
  const { data: assignments } = await db
    .from('program_assignments')
    .select('id, program_id, completed_at')
    .eq('assignee_user_id', assigneeUserId)
    .order('assigned_at', { ascending: false });

  if (!assignments || assignments.length === 0) return [];

  const programIds = assignments.map((a: any) => a.program_id);

  const [programsRes, activitiesRes, progressRes] = await Promise.all([
    db.from('programs').select('*').in('id', programIds),
    db.from('program_activities').select('id, program_id, position, title').in('program_id', programIds).order('position'),
    db.from('program_activity_progress').select('assignment_id, activity_id, status').in('assignment_id', assignments.map((a: any) => a.id)),
  ]);

  const programMap = new Map<string, Program>();
  (programsRes.data || []).forEach((row: any) => programMap.set(row.id, mapProgram(row)));

  const activitiesByProgram = new Map<string, { id: string; title: string; position: number }[]>();
  (activitiesRes.data || []).forEach((a: any) => {
    const list = activitiesByProgram.get(a.program_id) || [];
    list.push({ id: a.id, title: a.title, position: a.position });
    activitiesByProgram.set(a.program_id, list);
  });

  const completedByAssignment = new Map<string, Set<string>>();
  (progressRes.data || []).forEach((p: any) => {
    if (p.status === 'completed') {
      const set = completedByAssignment.get(p.assignment_id) || new Set();
      set.add(p.activity_id);
      completedByAssignment.set(p.assignment_id, set);
    }
  });

  const result: AssignedProgram[] = [];
  for (const assignment of assignments) {
    const program = programMap.get(assignment.program_id);
    if (!program) continue;

    const activities = activitiesByProgram.get(assignment.program_id) || [];
    const completedSet = completedByAssignment.get(assignment.id) || new Set();
    const completedCount = activities.filter((a) => completedSet.has(a.id)).length;
    const currentActivity = activities.find((a) => !completedSet.has(a.id));

    result.push({
      assignmentId: assignment.id,
      program,
      totalActivities: activities.length,
      completedActivities: completedCount,
      currentActivityTitle: currentActivity?.title || null,
    });
  }

  return result;
}

export async function fetchProgramForAssignee(assignmentId: string): Promise<{
  assignment: ProgramAssignment;
  program: ProgramDetail;
  progress: ProgramActivityProgress[];
  responses: ProgramActivityResponse[];
} | null> {
  const { data: assignmentRow } = await db
    .from('program_assignments')
    .select('*')
    .eq('id', assignmentId)
    .maybeSingle();

  if (!assignmentRow) return null;

  const program = await fetchProgramDetail(assignmentRow.program_id);
  if (!program) return null;

  const { data: progressRows } = await db
    .from('program_activity_progress')
    .select('*')
    .eq('assignment_id', assignmentId);

  const progress = (progressRows || []).map(mapProgress);

  const progressIds = progress.map((p: ProgramActivityProgress) => p.id);
  let responses: ProgramActivityResponse[] = [];
  if (progressIds.length > 0) {
    const { data: responseRows } = await db
      .from('program_activity_responses')
      .select('*')
      .in('progress_id', progressIds);
    responses = (responseRows || []).map(mapResponse);
  }

  return {
    assignment: mapAssignment(assignmentRow),
    program,
    progress,
    responses,
  };
}

// ============================================================
// PROGRESS / COMPLETION
// ============================================================

export async function upsertActivityProgress(
  assignmentId: string,
  activityId: string,
  status: ActivityStatus
): Promise<string> {
  const { data: existing } = await db
    .from('program_activity_progress')
    .select('id')
    .eq('assignment_id', assignmentId)
    .eq('activity_id', activityId)
    .maybeSingle();

  const now = new Date().toISOString();

  if (existing) {
    const payload: any = { status, updated_at: now };
    if (status === 'in_progress' && !existing.started_at) payload.started_at = now;
    if (status === 'completed') payload.completed_at = now;

    const { error } = await db.from('program_activity_progress').update(payload).eq('id', existing.id);
    if (error) throw error;
    return existing.id;
  } else {
    const payload: any = { assignment_id: assignmentId, activity_id: activityId, status };
    if (status === 'in_progress') payload.started_at = now;
    if (status === 'completed') { payload.started_at = now; payload.completed_at = now; }

    const { data: row, error } = await db
      .from('program_activity_progress')
      .insert(payload)
      .select('id')
      .single();

    if (error) throw error;
    return row.id;
  }
}

export async function upsertActivityResponse(
  progressId: string,
  activityId: string,
  responseText: string,
  uploadPath?: string,
  uploadFilename?: string
): Promise<void> {
  const { data: existing } = await db
    .from('program_activity_responses')
    .select('id')
    .eq('progress_id', progressId)
    .eq('activity_id', activityId)
    .maybeSingle();

  const now = new Date().toISOString();
  const payload: any = { response_text: responseText, updated_at: now };
  if (uploadPath !== undefined) payload.upload_path = uploadPath;
  if (uploadFilename !== undefined) payload.upload_filename = uploadFilename;

  if (existing) {
    const { error } = await db.from('program_activity_responses').update(payload).eq('id', existing.id);
    if (error) throw error;
  } else {
    const { error } = await db.from('program_activity_responses').insert({
      progress_id: progressId,
      activity_id: activityId,
      ...payload,
    });
    if (error) throw error;
  }
}

export async function markProgramComplete(assignmentId: string): Promise<void> {
  const { error } = await db
    .from('program_assignments')
    .update({ completed_at: new Date().toISOString() })
    .eq('id', assignmentId);
  if (error) throw error;
}

// ============================================================
// FILE UPLOAD
// ============================================================

export async function uploadProgramFile(userId: string, file: File): Promise<{ path: string; filename: string }> {
  const ext = file.name.split('.').pop() || 'bin';
  const path = `${userId}/${crypto.randomUUID()}.${ext}`;

  const { error } = await _supabase.storage.from('program-uploads').upload(path, file);
  if (error) throw error;

  return { path, filename: file.name };
}

export function getProgramFileUrl(uploadPath: string): string {
  const { data } = _supabase.storage.from('program-uploads').getPublicUrl(uploadPath);
  return data.publicUrl;
}

// ============================================================
// PROGRESS TRACKING FOR CREATORS
// ============================================================

export async function fetchProgramProgressForCreator(programId: string): Promise<{
  assignment: ProgramAssignment;
  progress: ProgramActivityProgress[];
  responses: ProgramActivityResponse[];
}[]> {
  const { data: assignments } = await db
    .from('program_assignments')
    .select('*')
    .eq('program_id', programId)
    .order('assigned_at', { ascending: false });

  if (!assignments || assignments.length === 0) return [];

  const userIds = assignments.map((a: any) => a.assignee_user_id);
  const { data: users } = await db
    .from('users')
    .select('id, full_name, email')
    .in('id', userIds);

  const userMap = new Map<string, { name: string; email: string }>();
  (users || []).forEach((u: any) => {
    userMap.set(u.id, { name: u.full_name || u.email || 'Unknown', email: u.email || '' });
  });

  const assignmentIds = assignments.map((a: any) => a.id);

  const [progressRes, responseRes] = await Promise.all([
    db.from('program_activity_progress').select('*').in('assignment_id', assignmentIds),
    db.from('program_activity_responses').select('*').in('progress_id',
      (await db.from('program_activity_progress').select('id').in('assignment_id', assignmentIds)).data?.map((p: any) => p.id) || []),
  ]);

  const progressByAssignment = new Map<string, ProgramActivityProgress[]>();
  (progressRes.data || []).forEach((p: any) => {
    const list = progressByAssignment.get(p.assignment_id) || [];
    list.push(mapProgress(p));
    progressByAssignment.set(p.assignment_id, list);
  });

  const responsesByProgress = new Map<string, ProgramActivityResponse[]>();
  (responseRes.data || []).forEach((r: any) => {
    const list = responsesByProgress.get(r.progress_id) || [];
    list.push(mapResponse(r));
    responsesByProgress.set(r.progress_id, list);
  });

  return assignments.map((a: any) => {
    const info = userMap.get(a.assignee_user_id) || { name: 'Unknown', email: '' };
    const progress = progressByAssignment.get(a.id) || [];
    const allResponses: ProgramActivityResponse[] = [];
    progress.forEach((p) => {
      const resp = responsesByProgress.get(p.id) || [];
      allResponses.push(...resp);
    });

    return {
      assignment: { ...mapAssignment(a), assigneeName: info.name, assigneeEmail: info.email },
      progress,
      responses: allResponses,
    };
  });
}
